'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/frontier'

const PipelineViz = dynamic(() => import('../_components/widgets/FrontierWidgets').then(m => m.PipelineViz), { ssr: false })
const PreferenceGame = dynamic(() => import('../_components/widgets/FrontierWidgets').then(m => m.PreferenceGame), { ssr: false })

export default function PretrainingToAssistant() {
  return (
    <Lesson>
      <Step gate="q-base">
        <H>A predictor is not an assistant</H>
        <P>
          The model you trained in the last module continues text. That’s all a <strong>base model</strong> does, no matter how
          big. Ask it “What is the capital of France?” and it may answer — or it may continue with three more quiz questions,
          because that’s what quiz pages on the internet look like.
        </P>
        <P>Turning a raw predictor into a helpful assistant happens in stages:</P>
        <Figure n="10.1" wide caption="From raw text predictor to assistant. Click each stage to see what data it uses and how the same question gets answered.">
          <PipelineViz />
        </Figure>
        <Quiz
          id="q-base"
          question="During supervised fine-tuning, what is the training objective?"
          options={[
            { label: 'Still next-token prediction — just on example conversations instead of web text.', correct: true },
            { label: 'A completely different loss designed for chat.', why: 'It’s the same cross-entropy on the next token. Only the data changes.' },
            { label: 'Maximising a reward.', why: 'That’s the preference-learning stage that comes after.' },
          ]}
        />
      </Step>

      <Step>
        <H>Learning what people prefer</H>
        <P>
          It’s hard to write down a perfect answer, but easy to say which of two answers is better. So people (and increasingly
          AI systems) compare pairs of responses. A <strong>reward model</strong> learns to give each response a score{' '}
          <M>r</M> such that preferred responses score higher, using the <strong>Bradley–Terry</strong> model:
        </P>
        <MB>{'P(A \\text{ preferred over } B) = \\sigma\\big(r(A) - r(B)\\big)'}</MB>
        <P>Be the human labeller. Each choice nudges the reward model — watch it work out what you value.</P>
        <Figure n="10.2" wide caption="Pick the better answer each round. The reward model (a simple linear one over four features) updates after every comparison and re-ranks all eight answers.">
          <PreferenceGame />
        </Figure>
      </Step>

      <Step gate="reward-model">
        <H>Train a reward model</H>
        <Exercise
          id="reward-model"
          title="Bradley–Terry from preferences"
          {...EX.rewardModel}
          hints={[
            <>
              For each pair: <C>p = sigmoid(vec.dot(w, answers[win]) - vec.dot(w, answers[lose]))</C>.
            </>,
            <>
              Then <C>w = vec.add(w, vec.scale(vec.sub(answers[win], answers[lose]), lr * (1 - p)))</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-rlhf">
        <H>Using the reward</H>
        <P>
          With a reward model in hand, <strong>RLHF</strong> (reinforcement learning from human feedback) lets the assistant
          generate answers, scores them with the reward model, and nudges the assistant toward higher-scoring behaviour — while a
          penalty keeps it from drifting too far from the fine-tuned model. Simpler alternatives like <strong>DPO</strong>{' '}
          skip the separate reward model and train directly on the preference pairs with a clever loss.
        </P>
        <Callout kind="warn">
          A reward model is only an approximation of what people want. Push too hard on it and the model can learn to exploit
          its blind spots — long-winded answers, flattery, confident tone — instead of actually being better. This is called{' '}
          <strong>reward hacking</strong>, and guarding against it is an active area of research.
        </Callout>
        <Quiz
          id="q-rlhf"
          question="Why keep the RLHF-trained model close to the original fine-tuned model?"
          options={[
            { label: 'So it can’t chase quirks of the imperfect reward model too far and lose its general abilities.', correct: true },
            { label: 'To make training faster.', why: 'It’s about safety and quality, not speed.' },
            { label: 'Because the reward model only works on short answers.', why: 'The concern is over-optimising an imperfect proxy.' },
          ]}
        />
      </Step>
    </Lesson>
  )
}
